import apiService, { defaultHeaders } from 'services/apiService';

export const OLDEST_YEAR = 2017;
export const NEWEST_YEAR = (new Date()).getFullYear();

export const QUARTERS = {
  quarter1: 'Janv-Mars',
  quarter2: 'Avr-Juin',
  quarter3: 'Juil-Sept',
  quarter4: 'Oct-Dec',
};

/**
 * getCategory object from a category value
 *
 * @param {string} category value
 * @returns {object} category object
 */
export const getCategory = (category, activityTypes) =>
  activityTypes.find(({ value: { category: categ } }) => categ === category);

/**
 * getSubCategory object from category and subcategory value
 *
 * @param {string} category and subcategory separated by comma
 * ex: 'sportEvent,cycling'
 * @returns {object} the category object (or empty object)
 */
export function getSubCategory (categoryTreeString, activityTypes) {
  const [, subcategory] = categoryTreeString.split(/,/);
  return activityTypes.find(({ key }) => key === `onf.fms.${subcategory}`).value.label || {};
}

/**
 * getDataFormatted
 *
 * @param {object} data form the server
 * ex:
 * [{
 *  name: 'zone1',
 *  data : {
 *    'quarter1': {
 *      'schoolOuting,noSporting': {events: 30, participants: 70},
 *      'sportEvent,cycling': {events: 12, participants: 120},
 *      ...
 *    }
 *    'quarter2': {...}
 *  }
 * },
 * {
 *  name: 'zone2',
 *  data: {
 *    'quarter1': {...}
 *  }
 * }]
 * @returns {object} data needed for graph and table views
 * ex:
 * {
 *  'zone1' : [
 *    {
 *      key: "zone1SpringschoolOuting,noSporting",
 *      season: "Printemps",
 *      category: "Sorties scolaires",
 *      subCategory: "Non sportive",
 *      events: 30,
 *      participants: 70
 *    },
 *    {
 *      key: "zone1SpringsportEvent,cycling",
 *      season: "Printemps",
 *      category: "Manifestation sportive",
 *      subCategory: "Cycliste",
 *      events: 12,
 *      participants: 120
 *    }
 *    ...
 *  ],
 *  'zone2': [...]
 * }
 */
export const getDataFormatted = (data, activityTypes) => {
  const dataFormatted = { 'Toutes les forêts': [] };
  Object.keys(data).length && data.forEach(stats => {
    Object.keys(stats.data).forEach(quarter => {
      Object.keys(stats.data[quarter]).forEach(category => {
        const { events, participants } = stats.data[quarter][category];
        const statsName = `${stats.name[0].toUpperCase()}${stats.name.slice(1)}`;
        if (!dataFormatted[stats.name]) {
          dataFormatted[statsName] = [];
        }
        const values = {
          key: `${stats.name}${quarter}${category}`,
          quarter: QUARTERS[quarter],
          category: getCategory(category.split(/,/)[0], activityTypes).value.categoryLabel,
          subCategory: getSubCategory(category, activityTypes),
          events,
          participants,
        };
        dataFormatted['Toutes les forêts'].push(values);
        dataFormatted[statsName].push(values);
      });
    });
  });
  return dataFormatted;
};

/**
 * getDataForStat
 *
 * @param {array} data form the server
 * @param {('events'|'participants')} displayKey values expected
 * @returns {array} formatted to the graphic input
 */
export const getDataForStat = (data, displayKey) => data.reduce((list, item) => {
  const { quarter, category } = item;
  const quarterAlreadyCreated = list.some(elem => elem.quarter === quarter);
  if (quarterAlreadyCreated) {
    return [
      ...list.map(elem => (
        (elem.quarter !== quarter)
          ? elem
          : (
            {
              ...elem,
              [category]:
                (!elem[category] ? 0 : elem[category]) + item[displayKey],
            }
          )
      )),
    ];
  }
  return [
    ...list,
    { quarter, [category]: item[displayKey] },
  ];
}, []);

/**
 * getDataKeysForStat
 *
 * @param {array} data form the server
 * @returns {array} all keys for graph keys
 */
export const getDataKeysForStat = data =>
  [...new Set(data.reduce((key, item) => key.concat(Object.keys(item)), []))];


/**
 * Return data from state
 * @param {number} year
 * @returns {object} data filter by year
 */
export const getStatData = async year => {
  try {
    const { data: { value } } = await apiService.request(`/datastore/onf.statistics.${year}/`, {
      headers: defaultHeaders,
    });
    return value;
  } catch (e) {
    return {};
  }
};



// WEBPACK FOOTER //
// ./src/helpers/statisticsHelpers.js